import { Injectable, inject } from '@angular/core';
import { Observable, of, shareReplay } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { DigitalProductService } from '../../digital-product/services/digital-product.service';
import { SubscriptionProductService } from '../../subscription-product/services/subscription-product.service';

export type PublicProductRouteType = 'subscription' | 'digitalProduct';

@Injectable({
  providedIn: 'root'
})
export class PublicProductRouteResolverService {
  private digitalProductService = inject(DigitalProductService);
  private subscriptionProductService = inject(SubscriptionProductService);
  private typeCache = new Map<string, Observable<PublicProductRouteType | null>>();
  private readonly guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  resolveType(login: string, identifier: string): Observable<PublicProductRouteType | null> {
    const normalizedLogin = (login ?? '').trim().toLowerCase();
    const normalizedIdentifier = (identifier ?? '').trim().toLowerCase();
    if (!normalizedLogin || !normalizedIdentifier) {
      return of(null);
    }

    const cacheKey = `${normalizedLogin}::${normalizedIdentifier}`;
    const cached = this.typeCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const request$ = this.resolveTypeInternal(login, identifier).pipe(
      shareReplay(1)
    );
    this.typeCache.set(cacheKey, request$);
    return request$;
  }

  private resolveTypeInternal(login: string, identifier: string): Observable<PublicProductRouteType | null> {
    const trimmedLogin = (login ?? '').trim();
    const trimmedIdentifier = (identifier ?? '').trim();
    if (!trimmedLogin || !trimmedIdentifier) {
      return of(null);
    }

    const subscriptionLookup$ = this.guidRegex.test(trimmedIdentifier)
      ? this.subscriptionProductService.getPublicById(trimmedIdentifier)
      : this.subscriptionProductService.getPublicByLoginAndSlug(trimmedLogin, trimmedIdentifier);

    return subscriptionLookup$.pipe(
      map((product) => this.hasValidId(product)),
      catchError(() => of(false)),
      switchMap((isSubscription) => {
        if (isSubscription) {
          return of('subscription' as const);
        }

        const digitalProductLookup$ = this.guidRegex.test(trimmedIdentifier)
          ? this.digitalProductService.getPublicById(trimmedIdentifier)
          : this.digitalProductService.getPublicByLoginAndSlug(trimmedLogin, trimmedIdentifier);

        return digitalProductLookup$.pipe(
          map((product) => this.hasValidId(product) ? 'digitalProduct' as const : null),
          catchError(() => of(null))
        );
      })
    );
  }

  private hasValidId(value: unknown): boolean {
    const id = (value as { id?: unknown } | null)?.id;
    return typeof id === 'string' && id.trim().length > 0;
  }
}
